import { type FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { isApiErrors } from '../../../models/ApiErrors'
import { type ValidationError, formatValidationErrorCode } from './parseParams'

export type ErrorClassification =
  | 'missing_params'
  | 'pkce_mismatch'
  | 'app_not_found'
  | 'not_enabled'
  | 'app_permission_denied'
  | 'authorize_failed'
  | 'unknown'

export interface ClassifiedError {
  classification: ErrorClassification
  alertVariant: 'danger' | 'warning'
  alertTitle: string
  alertBody: string
  errorCode: string
  actions: 'dashboard' | 'cancel_try_again'
}

export const classifyValidationErrors = (errors: ValidationError[]): ClassifiedError => {
  const hasPkce = errors.some((e) => e.field === 'pkce_mismatch')
  const missingCount = errors.filter((e) => e.type === 'missing').length

  const alertTitle = hasPkce ? 'App not recognized' : missingCount > 1 ? 'Multiple required parameters are missing' : 'App not recognized'

  return {
    classification: hasPkce ? 'pkce_mismatch' : 'missing_params',
    alertVariant: 'danger',
    alertTitle,
    alertBody: "This app isn't set up correctly to connect to Zocks. Contact the app's support team to fix the connection.",
    errorCode: formatValidationErrorCode(errors),
    actions: 'dashboard',
  }
}

export const classifyGetAppError = (error: FetchBaseQueryError): ClassifiedError => {
  const status = error.status as number | undefined
  const errorBody = error.data

  if (status === 404) {
    return {
      classification: 'app_not_found',
      alertVariant: 'danger',
      alertTitle: 'App not recognized',
      alertBody: "This app isn't set up correctly to connect to Zocks. Contact the app's support team to fix the connection.",
      errorCode: '404 · OAuth2 Application was not found',
      actions: 'dashboard',
    }
  }

  if (status === 400 && isApiErrors(errorBody) && errorBody.errors.length) {
    return {
      classification: 'not_enabled',
      alertVariant: 'warning',
      alertTitle: 'OAuth2 integrations are not available',
      alertBody: "Your account's plan doesn't include OAuth2 integrations. Contact your account owner to upgrade your plan.",
      errorCode: `400 · ${errorBody.errors[0].message}`,
      actions: 'dashboard',
    }
  }

  return {
    classification: 'unknown',
    alertVariant: 'danger',
    alertTitle: 'Something went wrong',
    alertBody: 'An unexpected error occurred. Please try again later.',
    errorCode: `${status ?? 'unknown'} · Unknown error`,
    actions: 'dashboard',
  }
}

interface OAuth2Error {
  error: string
  error_description: string
}

const isOAuth2Error = (value: unknown): value is OAuth2Error => {
  if (!value || typeof value !== 'object') {
    return false
  }
  const obj = value as Record<string, unknown>
  return typeof obj.error === 'string' && typeof obj.error_description === 'string'
}

export const classifyAuthorizeError = (error: FetchBaseQueryError): ClassifiedError => {
  const errorCode = isOAuth2Error(error.data)
    ? `${error.data.error} · ${error.data.error_description}`
    : typeof error.data === 'string'
      ? error.data
      : `${error.status ?? 'unknown'} · Unknown error`

  return {
    classification: 'authorize_failed',
    alertVariant: 'danger',
    alertTitle: 'Something went wrong with the connection',
    alertBody: "This is a setup issue on the connecting app's side. Contact your account admin or the app's support team for help.",
    errorCode,
    actions: 'cancel_try_again',
  }
}

export const buildAppPermissionDeniedError = (appName: string): ClassifiedError => ({
  classification: 'app_permission_denied',
  alertVariant: 'warning',
  alertTitle: `${appName} is not enabled for you`,
  alertBody: "Your account owner or admin hasn't enabled this app for you. Ask your account owner to enable it in Integrations > Apps.",
  errorCode: 'Permission denied',
  actions: 'dashboard',
})
